import { useMemo } from 'react';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ReferenceLine,
    CartesianGrid
} from 'recharts';
import { getDateAggregationKey } from '../../../utils/getDateAggregationKey';
import { WidgetWrapper } from './WidgetWrapper';
import { getColor } from '../../../utils/colorUtils';
import { downsample } from '../../../utils/performance';

interface DataPoint {
    time: string;
    value: number;
}

interface RollingSeries {
    label: string;
    points: DataPoint[];
}

interface Props {
    series: RollingSeries[];
}

export function RollingCorrelationWidget({ series }: Props) {
    const chartData = useMemo(() => {
        if (!series || series.length === 0) return [];

        const map = new Map<string, any>();
        series.forEach((s) => {
            s.points.forEach(p => {
                if (!map.has(p.time)) {
                    map.set(p.time, { time: p.time });
                }
                map.get(p.time)[s.label] = p.value;
            });
        });

        const merged = Array.from(map.values()).sort((a, b) => a.time.localeCompare(b.time));
        return merged.length > 1500 ? downsample(merged, 1000) : merged;
    }, [series]);

    if (!series || series.length === 0) {
        return (
            <WidgetWrapper title="📈 Rolling Correlation">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)' }}>
                    No correlation data available.
                </div>
            </WidgetWrapper>
        );
    }

    return (
        <WidgetWrapper title="📈 Rolling Correlation">
            <div style={{ width: '100%', height: '100%', padding: '0', display: 'flex', flexDirection: 'column' }}>
                <ResponsiveContainer width="100%" height="100%" style={{ flex: 1 }}>
                    <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-glass-border)" vertical={false} />
                        <XAxis
                            dataKey="time"
                            tickFormatter={(t) => getDateAggregationKey(t)}
                            tick={{ fill: 'var(--text-muted)', fontSize: 10 }}
                            stroke="var(--surface-glass-border)"
                        />
                        <YAxis
                            domain={[-1, 1]}
                            tick={{ fill: 'var(--text-muted)', fontSize: 10 }}
                            stroke="var(--surface-glass-border)"
                            ticks={[-1, -0.5, 0, 0.5, 1]}
                        />
                        <Tooltip
                            contentStyle={{
                                background: 'var(--bg-panel)',
                                border: '1px solid var(--surface-glass-border)',
                                borderRadius: '8px',
                                fontSize: '12px'
                            }}
                            labelFormatter={(l) => getDateAggregationKey(l, "datetime")}
                            animationDuration={0}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                        <ReferenceLine y={0} stroke="var(--text-muted)" strokeDasharray="3 3" />

                        {series.map((s, idx) => (
                            <Line
                                key={s.label}
                                type={chartData.length > 800 ? "linear" : "monotone"}
                                dataKey={s.label}
                                stroke={getColor(idx)}
                                dot={false}
                                strokeWidth={2}
                                isAnimationActive={false}
                                connectNulls={true}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <div style={{
                fontSize: '0.65rem',
                color: 'var(--text-muted)',
                textAlign: 'center',
                padding: '0.25rem 0',
                fontStyle: 'italic',
                flexShrink: 0
            }}>
                Rolling Pearson Correlation (Auto-Calculated Window)
            </div>
        </WidgetWrapper>
    );
}
